/**
 * DriftBoostSystem - NFS Underground Style Drift & NOS Boost Mechanik
 * 
 * Features:
 * - Drift-Punkte sammeln durch kontrolliertes Driften
 * - NOS-Boost für temporäre Geschwindigkeitserhöhung
 * - Drift-Multiplikator System
 * - Visuelle Effekte (Funken, Rauch, NOS-Flammen)
 */

import * as THREE from 'three';

export interface DriftState {
  isDrifting: boolean;
  driftAngle: number;
  driftDuration: number;
  driftScore: number;
  driftMultiplier: number;
  totalDriftPoints: number;
}

export interface BoostState {
  nitroAmount: number;       // 0-100%
  nitroActive: boolean;
  nitroRechargeRate: number;
  nitroDrainRate: number;
  boostPower: number;        // Geschwindigkeitsbonus
}

export interface DriftBoostConfig {
  // Drift Settings
  minDriftAngle: number;           // Mindestwinkel für Drift-Erkennung (Radians)
  maxDriftAngle: number;           // Maximaler Drift-Winkel
  driftScoreRate: number;          // Punkte pro Sekunde beim Driften
  driftMultiplierGrowth: number;   // Wie schnell der Multiplier wächst
  driftMultiplierDecay: number;    // Wie schnell der Multiplier sinkt
  maxDriftMultiplier: number;      // Maximaler Multiplier
  
  // Boost Settings
  nitroMaxAmount: number;          // Maximale NOS-Menge
  nitroRechargeRate: number;       // NOS Regeneration pro Sekunde
  nitroDrainRate: number;          // NOS Verbrauch pro Sekunde
  nitroBoostPower: number;         // Geschwindigkeitsbonus bei NOS
  nitroDriftRecharge: number;      // NOS-Bonus durch Driften
}

const DEFAULT_CONFIG: DriftBoostConfig = {
  minDriftAngle: 0.15,             // ~8.6 Grad
  maxDriftAngle: 1.2,              // ~69 Grad
  driftScoreRate: 100,             // 100 Punkte/Sekunde
  driftMultiplierGrowth: 0.5,      // +0.5x pro Sekunde
  driftMultiplierDecay: 2.0,       // -2x pro Sekunde wenn nicht drifting
  maxDriftMultiplier: 5.0,         // Max 5x Multiplier
  
  nitroMaxAmount: 100,
  nitroRechargeRate: 5,            // 5% pro Sekunde Regeneration
  nitroDrainRate: 25,              // 25% pro Sekunde bei Nutzung
  nitroBoostPower: 40,             // +40 km/h Speed Bonus
  nitroDriftRecharge: 15           // +15% NOS pro Drift-Combo
};

export class DriftBoostSystem {
  private config: DriftBoostConfig;
  
  private driftState: DriftState = {
    isDrifting: false,
    driftAngle: 0,
    driftDuration: 0,
    driftScore: 0,
    driftMultiplier: 1.0,
    totalDriftPoints: 0
  };
  
  private boostState: BoostState = {
    nitroAmount: 50,              // Start mit 50% NOS
    nitroActive: false,
    nitroRechargeRate: 5,
    nitroDrainRate: 25,
    boostPower: 0
  };
  
  // Visuelle Effekte
  private sparkParticles: THREE.Points | null = null;
  private smokeParticles: THREE.Points | null = null;
  private nitroFlames: THREE.Mesh[] = [];
  private scene: THREE.Scene | null = null;
  
  // Event Callbacks
  private onDriftStart?: () => void;
  private onDriftEnd?: (score: number) => void;
  private onNitroStart?: () => void;
  private onNitroEnd?: () => void;
  private onDriftCombo?: (multiplier: number, score: number) => void;
  
  constructor(config: Partial<DriftBoostConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.boostState.nitroRechargeRate = this.config.nitroRechargeRate;
    this.boostState.nitroDrainRate = this.config.nitroDrainRate;
  }
  
  /**
   * Attach visual effects to scene
   */
  attachToScene(scene: THREE.Scene): void {
    this.scene = scene;
    this.createSparkParticles();
    this.createSmokeParticles();
    this.createNitroFlames();
  }
  
  /**
   * Main update - call every frame
   */
  update(
    dt: number,
    carPosition: THREE.Vector3,
    carHeading: number,
    velocityDirection: number,
    speed: number,
    handbrakePressed: boolean,
    nitroPressed: boolean
  ): { speedBonus: number; gripModifier: number } {
    // Calculate drift angle
    const velocityAngle = velocityDirection;
    let driftAngle = Math.abs(this.normalizeAngle(carHeading - velocityAngle));
    
    // Only detect drift at sufficient speed and with handbrake
    const minDriftSpeed = 30;
    const isDrifting = speed > minDriftSpeed && 
                       handbrakePressed && 
                       driftAngle > this.config.minDriftAngle;
    
    // Update drift state
    this.updateDrift(dt, isDrifting, driftAngle, speed);
    
    // Update boost state
    this.updateBoost(dt, nitroPressed, speed);
    
    // Update visual effects
    this.updateEffects(dt, carPosition, carHeading, isDrifting, this.boostState.nitroActive);
    
    // Calculate modifiers
    const speedBonus = this.boostState.nitroActive ? this.config.nitroBoostPower : 0;
    const gripModifier = isDrifting ? 0.4 : 1.0; // Reduced grip while drifting
    
    return { speedBonus, gripModifier };
  }
  
  /**
   * Update drift mechanics
   */
  private updateDrift(dt: number, isDrifting: boolean, driftAngle: number, speed: number): void {
    const prevDrifting = this.driftState.isDrifting;
    
    this.driftState.isDrifting = isDrifting;
    this.driftState.driftAngle = driftAngle;
    
    if (isDrifting) {
      // Started drifting
      if (!prevDrifting) {
        this.onDriftStart?.();
      }
      
      this.driftState.driftDuration += dt;
      
      // Calculate score based on angle and speed
      const angleBonus = Math.min(driftAngle / this.config.maxDriftAngle, 1.0);
      const speedBonus = Math.min(speed / 150, 1.0);
      const scoreRate = this.config.driftScoreRate * angleBonus * speedBonus;
      
      // Apply multiplier
      this.driftState.driftScore += scoreRate * this.driftState.driftMultiplier * dt;
      
      // Grow multiplier
      this.driftState.driftMultiplier = Math.min(
        this.driftState.driftMultiplier + this.config.driftMultiplierGrowth * dt,
        this.config.maxDriftMultiplier
      );
      
      // Recharge NOS while drifting
      this.boostState.nitroAmount = Math.min(
        this.boostState.nitroAmount + this.config.nitroDriftRecharge * dt,
        this.config.nitroMaxAmount
      );
      
      // Trigger combo events
      if (Math.floor(this.driftState.driftMultiplier) > Math.floor(this.driftState.driftMultiplier - this.config.driftMultiplierGrowth * dt)) {
        this.onDriftCombo?.(this.driftState.driftMultiplier, this.driftState.driftScore);
      }
      
    } else {
      // Stopped drifting
      if (prevDrifting && this.driftState.driftScore > 0) {
        // Bank the drift score
        this.driftState.totalDriftPoints += Math.round(this.driftState.driftScore);
        this.onDriftEnd?.(Math.round(this.driftState.driftScore));
        
        // Reset drift tracking
        this.driftState.driftScore = 0;
        this.driftState.driftDuration = 0;
      }
      
      // Decay multiplier when not drifting
      this.driftState.driftMultiplier = Math.max(
        1.0,
        this.driftState.driftMultiplier - this.config.driftMultiplierDecay * dt
      );
    }
  }
  
  /**
   * Update boost/NOS mechanics
   */
  private updateBoost(dt: number, nitroPressed: boolean, speed: number): void {
    const prevActive = this.boostState.nitroActive;
    
    // Can only use NOS if we have some and are moving
    const canUseNitro = this.boostState.nitroAmount > 0 && speed > 5;
    this.boostState.nitroActive = nitroPressed && canUseNitro;
    
    if (this.boostState.nitroActive) {
      // Started NOS
      if (!prevActive) {
        this.onNitroStart?.();
      }
      
      // Drain NOS
      this.boostState.nitroAmount = Math.max(
        0,
        this.boostState.nitroAmount - this.boostState.nitroDrainRate * dt
      );
      
      // Set boost power
      this.boostState.boostPower = this.config.nitroBoostPower;
      
    } else {
      // Stopped NOS
      if (prevActive) {
        this.onNitroEnd?.();
      }
      
      this.boostState.boostPower = 0;
      
      // Passive NOS recharge (slower than drift recharge)
      if (!this.driftState.isDrifting) {
        this.boostState.nitroAmount = Math.min(
          this.boostState.nitroAmount + this.boostState.nitroRechargeRate * dt,
          this.config.nitroMaxAmount
        );
      }
    }
  }
  
  /**
   * Update visual effects
   */
  private updateEffects(
    dt: number,
    carPosition: THREE.Vector3,
    carHeading: number,
    isDrifting: boolean,
    nitroActive: boolean
  ): void {
    if (!this.scene) return;
    
    // Position spark particles at rear wheels when drifting
    if (this.sparkParticles) {
      this.sparkParticles.visible = isDrifting;
      if (isDrifting) {
        const offsetX = Math.sin(carHeading) * -1.5;
        const offsetZ = Math.cos(carHeading) * -1.5;
        this.sparkParticles.position.set(
          carPosition.x + offsetX,
          0.1,
          carPosition.z + offsetZ
        );
        
        // Animate particles
        const positions = (this.sparkParticles.geometry as THREE.BufferGeometry).getAttribute('position');
        for (let i = 0; i < positions.count; i++) {
          positions.setY(i, positions.getY(i) + (Math.random() - 0.5) * dt * 10);
          if (positions.getY(i) > 1 || positions.getY(i) < 0) {
            positions.setY(i, Math.random() * 0.5);
          }
        }
        positions.needsUpdate = true;
      }
    }
    
    // Smoke trail when drifting
    if (this.smokeParticles) {
      this.smokeParticles.visible = isDrifting;
      if (isDrifting) {
        const offsetX = Math.sin(carHeading) * -2;
        const offsetZ = Math.cos(carHeading) * -2;
        this.smokeParticles.position.set(
          carPosition.x + offsetX,
          0.3,
          carPosition.z + offsetZ
        );
      }
    }
    
    // NOS flames from exhaust
    this.nitroFlames.forEach((flame, i) => {
      flame.visible = nitroActive;
      if (nitroActive) {
        const side = i === 0 ? -0.4 : 0.4;
        const offsetX = Math.sin(carHeading) * -2 + Math.cos(carHeading) * side;
        const offsetZ = Math.cos(carHeading) * -2 - Math.sin(carHeading) * side;
        flame.position.set(
          carPosition.x + offsetX,
          0.4,
          carPosition.z + offsetZ
        );
        flame.rotation.y = carHeading + Math.PI;
        
        // Animate flame scale
        const scale = 0.8 + Math.sin(Date.now() * 0.02) * 0.3;
        flame.scale.set(scale, scale * 1.5, scale);
      }
    });
  }
  
  /**
   * Create spark particles for drift effect
   */
  private createSparkParticles(): void {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(100 * 3);
    const colors = new Float32Array(100 * 3);
    
    for (let i = 0; i < 100; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 2;
      positions[i * 3 + 1] = Math.random() * 0.5;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 2;
      
      // Orange/yellow sparks
      colors[i * 3] = 1.0;
      colors[i * 3 + 1] = 0.5 + Math.random() * 0.5;
      colors[i * 3 + 2] = 0;
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    const material = new THREE.PointsMaterial({
      size: 0.1,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      transparent: true,
      opacity: 0.8
    });
    
    this.sparkParticles = new THREE.Points(geometry, material);
    this.sparkParticles.visible = false;
    this.scene?.add(this.sparkParticles);
  }
  
  /**
   * Create smoke particles for tire smoke
   */
  private createSmokeParticles(): void {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(50 * 3);
    
    for (let i = 0; i < 50; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 3;
      positions[i * 3 + 1] = Math.random() * 1;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 3;
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    
    const material = new THREE.PointsMaterial({
      size: 0.5,
      color: 0x888888,
      transparent: true,
      opacity: 0.4,
      blending: THREE.NormalBlending
    });
    
    this.smokeParticles = new THREE.Points(geometry, material);
    this.smokeParticles.visible = false;
    this.scene?.add(this.smokeParticles);
  }
  
  /**
   * Create NOS flame meshes
   */
  private createNitroFlames(): void {
    const flameGeometry = new THREE.ConeGeometry(0.15, 0.8, 8);
    const flameMaterial = new THREE.MeshBasicMaterial({
      color: 0x00aaff,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending
    });
    
    // Two flames (left and right exhaust)
    for (let i = 0; i < 2; i++) {
      const flame = new THREE.Mesh(flameGeometry, flameMaterial);
      flame.rotation.x = Math.PI / 2;
      flame.visible = false;
      this.nitroFlames.push(flame);
      this.scene?.add(flame);
    }
  }
  
  /**
   * Normalize angle to -PI to PI
   */
  private normalizeAngle(angle: number): number {
    while (angle > Math.PI) angle -= Math.PI * 2;
    while (angle < -Math.PI) angle += Math.PI * 2;
    return angle;
  }
  
  // ===== Public Getters =====
  
  getDriftState(): Readonly<DriftState> {
    return { ...this.driftState };
  }
  
  getBoostState(): Readonly<BoostState> {
    return { ...this.boostState };
  }
  
  getTotalDriftPoints(): number {
    return this.driftState.totalDriftPoints;
  }
  
  getCurrentDriftScore(): number {
    return Math.round(this.driftState.driftScore);
  }
  
  getDriftMultiplier(): number {
    return this.driftState.driftMultiplier;
  }
  
  getNitroPercentage(): number {
    return this.boostState.nitroAmount;
  }
  
  isNitroActive(): boolean {
    return this.boostState.nitroActive;
  }
  
  isDrifting(): boolean {
    return this.driftState.isDrifting;
  }
  
  // ===== Event Registration =====
  
  onDriftStarted(callback: () => void): void {
    this.onDriftStart = callback;
  }
  
  onDriftEnded(callback: (score: number) => void): void {
    this.onDriftEnd = callback;
  }
  
  onNitroStarted(callback: () => void): void {
    this.onNitroStart = callback;
  }
  
  onNitroEnded(callback: () => void): void {
    this.onNitroEnd = callback;
  }
  
  onDriftComboReached(callback: (multiplier: number, score: number) => void): void {
    this.onDriftCombo = callback;
  }
  
  // ===== Utility =====
  
  reset(): void {
    this.driftState = {
      isDrifting: false,
      driftAngle: 0,
      driftDuration: 0,
      driftScore: 0,
      driftMultiplier: 1.0,
      totalDriftPoints: 0
    };
    
    this.boostState.nitroAmount = 50;
    this.boostState.nitroActive = false;
    this.boostState.boostPower = 0;
  }
  
  /**
   * Add NOS manually (e.g., from pickup)
   */
  addNitro(amount: number): void {
    this.boostState.nitroAmount = Math.min(
      this.boostState.nitroAmount + amount,
      this.config.nitroMaxAmount
    );
  }
  
  /**
   * Dispose resources
   */
  dispose(): void {
    if (this.sparkParticles) {
      this.scene?.remove(this.sparkParticles);
      this.sparkParticles.geometry.dispose();
      (this.sparkParticles.material as THREE.Material).dispose();
    }
    
    if (this.smokeParticles) {
      this.scene?.remove(this.smokeParticles);
      this.smokeParticles.geometry.dispose();
      (this.smokeParticles.material as THREE.Material).dispose();
    }
    
    this.nitroFlames.forEach(flame => {
      this.scene?.remove(flame);
      flame.geometry.dispose();
      (flame.material as THREE.Material).dispose();
    });
    
    this.nitroFlames = [];
    this.scene = null;
  }
}

// ===== Export Drift/Boost HUD Data Interface =====
export interface DriftBoostHUDData {
  isDrifting: boolean;
  driftScore: number;
  driftMultiplier: number;
  totalDriftPoints: number;
  nitroPercentage: number;
  nitroActive: boolean;
}

export function getDriftBoostHUDData(system: DriftBoostSystem): DriftBoostHUDData {
  return {
    isDrifting: system.isDrifting(),
    driftScore: system.getCurrentDriftScore(),
    driftMultiplier: system.getDriftMultiplier(),
    totalDriftPoints: system.getTotalDriftPoints(),
    nitroPercentage: system.getNitroPercentage(),
    nitroActive: system.isNitroActive()
  };
}
